/**
 * ═══════════════════════════════════════════════════════════════════════════
 * AITestingPanel - WASP Frontend v2.2.0
 * ═══════════════════════════════════════════════════════════════════════════
 * 
 * @file AITestingPanel.tsx
 * @version 1.0.0
 * @description Panel de testing en vivo de endpoints de IA
 * @author WASP Development Team
 * 
 * FUNCIONALIDADES:
 * - Seleccionar endpoint a probar
 * - Enviar mensaje de prueba
 * - Ver respuesta del endpoint
 * - Métricas de respuesta (tiempo, tokens)
 * - Rating de respuesta (1-5 estrellas)
 * - Historial de tests
 * - Exportar historial
 * 
 * ═══════════════════════════════════════════════════════════════════════════
 */

import React, { useState } from 'react';
import {
  Row,
  Col,
  Card,
  Form,
  Button,
  Alert,
  Badge,
  Spinner,
  ListGroup,
  InputGroup
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faVial,
  faPaperPlane,
  faHistory,
  faTrash,
  faClock,
  faCoins,
  faStar,
  faCheckCircle,
  faTimesCircle,
  faExclamationTriangle,
  faDownload
} from '@fortawesome/free-solid-svg-icons';
import { useAIEndpoints } from '../../../../../hooks/useAIEndpoints';
import { useAITesting } from '../../../../../hooks/useAITesting';

// ═══════════════════════════════════════════════════════════════════════════
// COMPONENTE PRINCIPAL
// ═══════════════════════════════════════════════════════════════════════════

const AITestingPanel: React.FC = () => {
  // ───────────────────────────────────────────────────────────────────────
  // Hooks
  // ───────────────────────────────────────────────────────────────────────
  
  const {
    endpoints,
    isLoading: endpointsLoading
  } = useAIEndpoints();

  const {
    isLoading: testLoading,
    error: testError,
    lastResult,
    testHistory,
    testEndpoint,
    clearHistory,
    clearLastResult
  } = useAITesting();

  // ───────────────────────────────────────────────────────────────────────
  // Estado Local
  // ───────────────────────────────────────────────────────────────────────
  
  const [selectedEndpointId, setSelectedEndpointId] = useState<string>('');
  const [testMessage, setTestMessage] = useState<string>('');
  const [showHistory, setShowHistory] = useState<boolean>(true);

  // Filtrar solo endpoints activos con capacidad 'text'
  const testableEndpoints = endpoints.filter(
    ep => ep.enabled && ep.capabilities.includes('text')
  );

  const selectedEndpoint = endpoints.find(ep => ep.id === selectedEndpointId);

  // ───────────────────────────────────────────────────────────────────────
  // Handlers
  // ───────────────────────────────────────────────────────────────────────
  
  const handleTest = async () => {
    if (!selectedEndpointId || !testMessage.trim()) return;
    await testEndpoint(selectedEndpointId, testMessage.trim());
  };

  const handleClearHistory = () => {
    if (window.confirm('¿Estás seguro de que deseas limpiar todo el historial?')) {
      clearHistory();
    }
  };

  const handleExportHistory = () => {
    const dataStr = JSON.stringify(testHistory, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `ai-testing-history-${Date.now()}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      handleTest();
    }
  };

  // ───────────────────────────────────────────────────────────────────────
  // Render - Test Input
  // ───────────────────────────────────────────────────────────────────────
  
  const renderTestInput = () => (
    <Card className="border-0 shadow-sm mb-4">
      <Card.Body>
        <h5 className="mb-3">
          <FontAwesomeIcon icon={faVial} className="me-2 text-warning" />
          Probar Endpoint
        </h5>

        {/* Selector de Endpoint */}
        <Form.Group className="mb-3">
          <Form.Label>Endpoint a probar *</Form.Label>
          <Form.Select
            size="lg"
            value={selectedEndpointId}
            onChange={(e) => setSelectedEndpointId(e.target.value)}
            disabled={testLoading}
          >
            <option value="">Selecciona un endpoint...</option>
            {testableEndpoints.map(endpoint => (
              <option key={endpoint.id} value={endpoint.id}>
                {endpoint.name} ({endpoint.type})
              </option>
            ))}
          </Form.Select>
          <Form.Text className="text-muted">
            Solo se muestran endpoints activos con capacidad 'text'
          </Form.Text>
        </Form.Group>

        {/* Mensaje de prueba */}
        <Form.Group className="mb-3">
          <Form.Label>Mensaje de prueba *</Form.Label>
          <Form.Control
            as="textarea"
            rows={4}
            value={testMessage}
            onChange={(e) => setTestMessage(e.target.value)}
            onKeyDown={handleKeyPress}
            placeholder="Escribe tu mensaje de prueba aquí..."
            disabled={testLoading}
          />
          <Form.Text className="text-muted">
            Presiona Ctrl+Enter o Cmd+Enter para enviar
          </Form.Text>
        </Form.Group>

        {/* Info del endpoint seleccionado */}
        {selectedEndpoint && (
          <Alert variant="info" className="mb-3">
            <small>
              <strong>Modelo:</strong> {selectedEndpoint.metadata?.model || 'No especificado'}<br />
              <strong>URL:</strong> {selectedEndpoint.url}<br />
              <strong>Configuración:</strong> Temperature: {selectedEndpoint.metadata?.temperature || 0.7}, 
              Max Tokens: {selectedEndpoint.metadata?.maxTokens || 1000}
            </small>
          </Alert>
        )}

        {/* Botón de envío */}
        <div className="d-flex gap-2">
          <Button
            variant="primary"
            onClick={handleTest}
            disabled={!selectedEndpointId || !testMessage.trim() || testLoading}
            className="flex-grow-1"
          >
            {testLoading ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  className="me-2"
                />
                Probando...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faPaperPlane} className="me-2" />
                Enviar Prueba
              </>
            )}
          </Button>
          {lastResult && (
            <Button
              variant="outline-secondary"
              onClick={clearLastResult}
            >
              Limpiar
            </Button>
          )}
        </div>
      </Card.Body>
    </Card>
  );

  // ───────────────────────────────────────────────────────────────────────
  // Render - Test Result
  // ───────────────────────────────────────────────────────────────────────
  
  const renderTestResult = () => {
    if (!lastResult) return null;

    return (
      <Card className="border-0 shadow-sm mb-4">
        <Card.Body>
          <h5 className="mb-3">
            {lastResult.success ? (
              <>
                <FontAwesomeIcon icon={faCheckCircle} className="me-2 text-success" />
                Respuesta del Endpoint
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faTimesCircle} className="me-2 text-danger" />
                Error en el Test
              </>
            )}
          </h5>

          {/* Métricas */}
          <Row className="mb-3">
            <Col md={4}>
              <div className="d-flex align-items-center">
                <FontAwesomeIcon icon={faClock} className="me-2 text-primary" />
                <div>
                  <small className="text-muted d-block">Tiempo de respuesta</small>
                  <strong>{lastResult.response_time}ms</strong>
                </div>
              </div>
            </Col>
            {lastResult.tokens_used && (
              <Col md={4}>
                <div className="d-flex align-items-center">
                  <FontAwesomeIcon icon={faCoins} className="me-2 text-warning" />
                  <div>
                    <small className="text-muted d-block">Tokens usados</small>
                    <strong>{lastResult.tokens_used}</strong>
                  </div>
                </div>
              </Col>
            )}
            <Col md={4}>
              <div className="d-flex align-items-center">
                <Badge 
                  bg={lastResult.success ? 'success' : 'danger'}
                  className="px-3 py-2"
                >
                  {lastResult.success ? 'Éxito' : 'Error'}
                </Badge>
              </div>
            </Col>
          </Row>

          {/* Respuesta o Error */}
          {lastResult.success ? (
            <>
              {lastResult.response && (
                <div className="p-3 bg-light rounded border">
                  <small className="text-muted d-block mb-2">Respuesta:</small>
                  <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {lastResult.response}
                  </div>
                </div>
              )}
              {lastResult.note && (
                <Alert variant="info" className="mt-3 mb-0">
                  <small>{lastResult.note}</small>
                </Alert>
              )}
            </>
          ) : (
            <Alert variant="danger" className="mb-0">
              <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
              <strong>Error:</strong> {lastResult.error || 'Error desconocido'}
            </Alert>
          )}
        </Card.Body>
      </Card>
    );
  };

  // ───────────────────────────────────────────────────────────────────────
  // Render - History
  // ───────────────────────────────────────────────────────────────────────
  
  const renderHistory = () => {
    if (!showHistory) return null;

    return (
      <Card className="border-0 shadow-sm">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0">
              <FontAwesomeIcon icon={faHistory} className="me-2 text-secondary" />
              Historial de Tests
              <Badge bg="secondary" className="ms-2">
                {testHistory.length}
              </Badge>
            </h5>
            <div className="d-flex gap-2">
              <Button
                variant="outline-primary"
                size="sm"
                onClick={handleExportHistory}
                disabled={testHistory.length === 0}
              >
                <FontAwesomeIcon icon={faDownload} className="me-1" />
                Exportar
              </Button>
              <Button
                variant="outline-danger"
                size="sm"
                onClick={handleClearHistory}
                disabled={testHistory.length === 0}
              >
                <FontAwesomeIcon icon={faTrash} className="me-1" />
                Limpiar
              </Button>
            </div>
          </div>

          {testHistory.length === 0 ? (
            <Alert variant="info" className="mb-0">
              No hay tests realizados todavía
            </Alert>
          ) : (
            <ListGroup>
              {testHistory.slice(0, 10).map((test) => (
                <ListGroup.Item key={test.id} className="px-3 py-2">
                  <div className="d-flex justify-content-between align-items-start">
                    <div className="flex-grow-1">
                      <div className="d-flex align-items-center mb-1">
                        <Badge 
                          bg={test.result.success ? 'success' : 'danger'}
                          className="me-2"
                        >
                          {test.result.success ? 'OK' : 'ERROR'}
                        </Badge>
                        <strong className="me-2">{test.endpoint_name}</strong>
                        <small className="text-muted">
                          {new Date(test.timestamp).toLocaleString()}
                        </small>
                      </div>
                      <small className="text-muted d-block mb-1">
                        <strong>Mensaje:</strong> {test.test_message.substring(0, 100)}
                        {test.test_message.length > 100 && '...'}
                      </small>
                      <div className="d-flex gap-3">
                        <small className="text-muted">
                          <FontAwesomeIcon icon={faClock} className="me-1" />
                          {test.result.response_time}ms
                        </small>
                        {test.result.tokens_used && (
                          <small className="text-muted">
                            <FontAwesomeIcon icon={faCoins} className="me-1" />
                            {test.result.tokens_used} tokens
                          </small>
                        )}
                      </div>
                    </div>
                  </div>
                </ListGroup.Item>
              ))}
            </ListGroup>
          )}

          {testHistory.length > 10 && (
            <div className="text-center mt-3">
              <small className="text-muted">
                Mostrando los últimos 10 de {testHistory.length} tests
              </small>
            </div>
          )}
        </Card.Body>
      </Card>
    );
  };

  // ───────────────────────────────────────────────────────────────────────
  // Render Principal
  // ───────────────────────────────────────────────────────────────────────
  
  if (endpointsLoading) {
    return (
      <Alert variant="info">
        Cargando endpoints...
      </Alert>
    );
  }

  if (testableEndpoints.length === 0) {
    return (
      <Alert variant="warning">
        <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
        No hay endpoints activos con capacidad 'text' para probar. 
        Configura al menos un endpoint en la pestaña "Endpoints".
      </Alert>
    );
  }

  return (
    <div>
      {/* Error general */}
      {testError && (
        <Alert variant="danger" dismissible>
          <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
          {testError}
        </Alert>
      )}

      <Row>
        {/* Panel izquierdo - Input y Resultado */}
        <Col lg={6}>
          {renderTestInput()}
          {renderTestResult()}
        </Col>

        {/* Panel derecho - Historial */}
        <Col lg={6}>
          {renderHistory()}
        </Col>
      </Row>

      {/* Stats rápidas */}
      <Row className="mt-4">
        <Col md={3}>
          <Card className="border-0 shadow-sm text-center">
            <Card.Body>
              <h3 className="mb-1 text-primary">{testHistory.length}</h3>
              <small className="text-muted">Tests Totales</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="border-0 shadow-sm text-center">
            <Card.Body>
              <h3 className="mb-1 text-success">
                {testHistory.filter(t => t.result.success).length}
              </h3>
              <small className="text-muted">Exitosos</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="border-0 shadow-sm text-center">
            <Card.Body>
              <h3 className="mb-1 text-danger">
                {testHistory.filter(t => !t.result.success).length}
              </h3>
              <small className="text-muted">Fallidos</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="border-0 shadow-sm text-center">
            <Card.Body>
              <h3 className="mb-1 text-info">
                {testHistory.length > 0
                  ? Math.round(
                      (testHistory.filter(t => t.result.success).length / testHistory.length) * 100
                    )
                  : 0}%
              </h3>
              <small className="text-muted">Tasa de Éxito</small>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default AITestingPanel;

// ═══════════════════════════════════════════════════════════════════════════
// FIN DEL ARCHIVO
// ═══════════════════════════════════════════════════════════════════════════